import {
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatChipsModule } from '@angular/material/chips';
import { TextfieldModule } from '@ed---interne/ng-uui-components';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

type Option = { value: string; label: string; disabled?: boolean };

@Component({
    selector: 'ed-search',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TextfieldModule,
        MatAutocompleteModule,
        MatOptionModule,
        MatChipsModule,
    ],
    templateUrl: './ed-search.component.html',
    styleUrls: ['./ed-search.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EDSearchComponent),
            multi: true,
        },
    ],
})
export class EDSearchComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
    /* ===== Constante de debounce ===== */
    private static readonly SEARCH_DEBOUNCE_MS = 250;

    /* ------- Inputs ------- */
    @Input() placeholder = 'Rechercher…';
    @Input() isMulti = false;
    @Input() icon?: string;
    @Input() suffixIcon?: string;

    /* Déduplication des options + expose safeOptions pour le template */
    private _safeOptions: Option[] = [];
    @Input() set options(v: Option[]) {
        const arr = Array.isArray(v) ? v : [];
        // Déduplique par value, conserve le 1er
        const seen = new Set<string>();
        const next: Option[] = [];
        for (const o of arr) {
            if (!o || typeof o.value !== 'string') continue;
            if (seen.has(o.value)) continue;
            seen.add(o.value);
            next.push(o);
        }
        // Ne remplace que si réellement différent pour éviter des ngOnChanges en cascade
        if (!arraysEqualOptions(this._safeOptions, next)) {
            this._safeOptions = next;
        }
    }
    get safeOptions(): Option[] {
        return this._safeOptions.filter(
            (o) =>
                containsInsensitive(o.label, this.inputText) ||
                containsInsensitive(o.value, this.inputText),
        );
    }

    /** Alternative sans CVA : toujours un tableau */
    @Input() selectedValues: string[] = [];

    /** Disabled (bindable) */
    @Input() set disabled(v: boolean) {
        this._disabled = !!v;
        this.disabledChange.emit(this._disabled);
    }
    get disabled() {
        return this._disabled;
    }
    private _disabled = false;

    /* ------- Outputs ------- */
    @Output() search = new EventEmitter<string>(); // émis avec debounce
    @Output() selectionsChange = new EventEmitter<string[]>(); // toujours une liste
    @Output() disabledChange = new EventEmitter<boolean>();

    /* ------- Modèle interne ------- */
    inputText = '';
    public selected: string[] = []; // source de vérité (toujours array)

    /* ------- Debounce ------- */
    private searchInput$ = new Subject<string>();
    private sub?: Subscription;

    /* ------- CVA ------- */
    private onChange: (val: string | string[] | null) => void = () => {};
    private onTouched: () => void = () => {};
    /** Garde-fou pour éviter les boucles lors des MAJ externes */
    private _patchingFromOutside = false;

    // ===== Lifecycle =====
    ngOnInit(): void {
        this.sub = this.searchInput$
            .pipe(debounceTime(EDSearchComponent.SEARCH_DEBOUNCE_MS), distinctUntilChanged())
            .subscribe((q) => {
                this.search.emit(q);
            });
    }
    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    ngOnChanges(ch: SimpleChanges): void {
        // Miroir [(selectedValues)] => on synchronise SANS réémettre ni propager vers CVA
        if ('selectedValues' in ch) {
            const arr = Array.isArray(this.selectedValues) ? this.selectedValues : [];
            if (!arraysEqual(this.selected, arr)) {
                this._patchingFromOutside = true;
                this.selected = [...arr];
                this.inputText = !this.isMulti ? (this.labelOf(this.selected[0]) ?? '') : '';
                // Pas de selectionsChange (vient déjà du parent), pas de onChange ici
                this._patchingFromOutside = false;
            }
        }
    }

    // ===== CVA =====
    writeValue(obj: any): void {
        // Update interne SANS émettre vers le parent (sinon boucle)
        this._patchingFromOutside = true;
        if (Array.isArray(obj)) this.selected = [...obj];
        else if (obj == null || obj === '') this.selected = [];
        else this.selected = [obj];

        this.inputText = !this.isMulti ? (this.labelOf(this.selected[0]) ?? '') : '';
        this._patchingFromOutside = false;
    }
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    // ===== UI / handlers =====
    onTextChange(txt: string) {
        //Fix:
        if (txt === '[object Object]') return;
        this.inputText = txt;
        this.searchInput$.next(txt); // <-- debounced output

        if (!this.isMulti && !txt) {
            // clear en single
            if (this.selected.length) {
                this.selected = [];
                this.emitSelectionsUser(); // ← émission côté utilisateur
                this.propagateToCvaUser();
            }
        }
    }

    selectOption(ev: MatAutocompleteSelectedEvent) {
        const opt = ev.option.value as Option;
        if (!opt) return;

        if (this.isMulti) {
            if (!this.selected.includes(opt.value)) {
                this.selected = [...this.selected, opt.value];
                this.emitSelectionsUser();
                this.propagateToCvaUser();
            }
            this.inputText = '';
        } else {
            // single
            if (this.selected[0] !== opt.value) {
                this.selected = [opt.value];
                this.emitSelectionsUser();
                this.propagateToCvaUser();
            }
            this.inputText = opt.label;
        }
    }

    removeChip(val: string) {
        if (this.disabled) return;
        if (this.selected.includes(val)) {
            this.selected = this.selected.filter((v) => v !== val);
            this.emitSelectionsUser();
            this.propagateToCvaUser();
        }
    }

    isSelected(val: string) {
        return this.selected.includes(val);
    }
    labelOf(val?: string | null): string | null {
        if (!val) return null;
        return this._safeOptions.find((o) => o.value === val)?.label ?? null;
    }

    // ===== propagation (séparer "user" vs "outside") =====
    private emitSelectionsUser() {
        if (this._patchingFromOutside) return;
        this.selectionsChange.emit([...this.selected]);
    }
    private propagateToCvaUser() {
        if (this._patchingFromOutside) return;
        if (this.isMulti) this.onChange([...this.selected]);
        else this.onChange(this.selected[0] ?? null);
    }
}

/* ===== Helpers d’égalité ===== */
function arraysEqual(a: string[], b: string[]) {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}
function arraysEqualOptions(a: Option[], b: Option[]) {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i].value !== b[i].value || a[i].label !== b[i].label) return false;
    }
    return true;
}

function normalize(str: string): string {
    return str
        .normalize('NFD') // décompose les caractères
        .replace(/[\u0300-\u036f]/g, '') // retire les diacritiques
        .toLowerCase();
}

function containsInsensitive(haystack: string, needle: string): boolean {
    return normalize(haystack).includes(normalize(needle));
}
